import React, { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  FaEye,
  FaBullseye,
  FaShieldAlt,
  FaStar,
  FaCheckCircle,
  FaIndustry,
  FaTools,
  FaBuilding,
  FaPhoneAlt,
} from "react-icons/fa";
import "./aboutus.css";
import { Link } from "react-router-dom";

/* ================== ANIMATIONS ================== */
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.2 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.6, ease: "backOut" },
  },
  hover: { scale: 1.05, y: -8 },
};

/* ================== COUNT UP ================== */
const CountUp = ({ end, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const step = end / 80;

    const timer = setInterval(() => {
      start += step;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.ceil(start));
      }
    }, 20);

    return () => clearInterval(timer);
  }, [isInView, end]);

  return (
    <span ref={ref}>
      {count.toLocaleString()}
      {suffix}
    </span>
  );
};

/* ================== MAIN COMPONENT ================== */
function AboutUs() {
  const stats = [
    { number: 30, suffix: "+", label: "Years of Experience" },
    { number: 1000, suffix: "+", label: "Completed Projects" },
    { number: 100, suffix: "%", label: "Quality Commitment" },
    { number: 65, suffix: "+", label: "Product Solutions" },
  ];

  const services = [
    "Aluminium doors, windows, partitions, fanlights, canopies, wall cladding, ceilings",
    "Pre-Fabrication Solusions like Commercial Buildings, WareHouses and Cabanas",
    "Importing and selling of Induatrial machinery, agricultural machinery and equipment",
  ];

  return (
    <div className="about-main">
      {/* ================= HERO ================= */}
      <section className="about-hero">
        <div className="container-about">
          <motion.div
            className="about-hero-content"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
<motion.div className="about-hero-text" variants={itemVariants}>
  <h1 className="about-hero-title">
    Your Trusted{" "}
    <span className="gradient-text">
      Pre-Fabrication Partner
    </span>
  </h1>

  <p className="about-hero-description">
    INNOVATIVE PRE-FAB TECHNOLOGY is committed to delivering exceptional 
    aluminium-glass and steel fabrication solutions. We strive for high 
    performance through continuous improvement in product innovation, 
    quality standards, reliability, and outstanding customer service.
  </p>
</motion.div>


            <motion.div className="about-hero-image" variants={itemVariants}>
              <img
                src="/alu collect.png"
                alt="Innovative Pre-Fab Technology"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ================= VISION & MISSION ================= */}
      <section className="vision-mission-section">
        <div className="container">
          <motion.div
            className="vision-mission-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {/* Vision */}
            <motion.div className="vision-card" variants={cardVariants}>
              <div className="card-icon vision-icon">
                <FaEye />
              </div>
              <h3>Our Vision</h3>
              <p>
                Modern buildings require the design and 
                construction of fenestration systems with 
                high standards in this rapidly changing world. 
                By focusing always on the specifications and 
                the requirements of the most advanced 
                markets worldwide, INNOVATIVE PRE-FAB 
                TECHNOLOGY has created a product 
                portfolio which incorporates innovative 
                products and responds perfectly to the 
                modern building needs.
              </p>
            </motion.div>

            {/* Mission */}
            <motion.div className="mission-card" variants={cardVariants}>
              <div className="card-icon mission-icon">
                <FaBullseye />
              </div>
              <h3>Our Mission</h3>
              <p>
                We always make sure how to offer the best possible solution 
                for customer requirements and maximize 
                the value for our customers. The INNOVATIVE PRE-FAB 
                TECHNOLOGY is a well renowned name for Aluminium fabrication, 
                enhancing the appearance of any type of buildings like hotels,buildings, houses, office etc.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ================= OUR STORY ================= */}
      <section className="our-story">
        <div className="container">
          <motion.div
            className="story-content"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div className="story-image" variants={itemVariants}>
              <img src="/Alutech Alumium.png" alt="Our Journey" />
            </motion.div>

           <motion.div className="story-text" variants={itemVariants}>
  <h2>Our Journey</h2>
  <p>
    The INNOVATIVE PRE-FAB TECHNOLOGY is a well-renowned name in Sri Lanka 
    for aluminium-glass and steel fabrication solutions. We enhance the 
    appearance and performance of houses, office buildings, and commercial 
    complexes with modern pre-fabricated systems.
  </p>
  <p>
    Our journey is built on a strong foundation of excellence, innovation, 
    and customer satisfaction. We continuously evolve to meet the changing 
    demands of the construction industry while maintaining the highest standards of quality, 
    safety, craftsmanship, and guarantee.
  </p>
</motion.div>

          </motion.div>
        </div>
      </section>

      {/* ================= STATS ================= */}
      <section className="stats-section">
        <div className="container">
          <motion.div
            className="stats-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {stats.map((stat, i) => (
              <motion.div key={i} className="stat-card" variants={cardVariants}>
                <div className="stat-number">
                  <CountUp end={stat.number} suffix={stat.suffix} />
                </div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= SERVICES ================= */}
      <section className="mission-section">
        <div className="container">
          <motion.div
            className="mission-content"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.h2 variants={itemVariants}>
              Main Business Activities
            </motion.h2>

            <motion.div className="mission-points" variants={itemVariants}>
              {services.map((item, index) => (
                <div key={index} className="mission-point">
                  <FaCheckCircle className="point-icon" />
                  <span>{item}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section className="about-cta">
        <div className="container">
          <motion.div
            className="about-cta-content"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2>Ready to Build with Confidence?</h2>
            <p>
           Partner with INNOVATIVE PRE-FAB TECHNOLOGY for durable, modern, and 
           Innovative pre-fab warehouses, houses, cabanas, and aluminium fabrication solutions.
            </p>

            <div className="cta-buttons">
              <Link to="/get-a-quote">
                <button className="btn btn-primary-book-now">
                  Request a Quote
                </button>
              </Link>

              <button
                className="btn btn-secondary-call-about"
                onClick={() => (window.location.href = "tel:+94777324632")}
              >
                <FaPhoneAlt /> Call Us
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default AboutUs;